import React, { useEffect, useState, memo, Component } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { HomeIcon, RadioIcon, HeartHandshakeIcon, GiftIcon, UserIcon, ChevronLeftIcon, TrophyIcon } from 'lucide-react';
// Loading Screen Component
const LoadingScreen = () => {
  return <div className="flex flex-col items-center justify-center w-full h-screen bg-white" data-id="element-0">
      <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/g4gEPf1J1F25W6MQmHg53a/Geneva_Logo_-_Print_500px.jpg" alt="Geneva School of Boerne Logo" className="w-64 h-auto mb-8" data-id="element-1" />
      <div className="w-64 h-1 bg-gray-200 rounded-full overflow-hidden" data-id="element-2">
        <div className="w-1/2 h-full bg-[#002147] animate-pulse" data-id="element-3"></div>
      </div>
    </div>;
};
// Back Button Component
const BackButton = () => {
  const navigate = useNavigate();
  return <button onClick={() => navigate(-1)} className="p-2 text-[#002147]" aria-label="Go back" data-id="element-4">
      <ChevronLeftIcon size={24} data-id="element-5" />
    </button>;
};
// Now Playing Bar Component
const NowPlayingBar = ({
  isPlaying,
  setIsPlaying,
  currentPodcast
}) => {
  if (!isPlaying) return null;
  return <div className="fixed bottom-16 left-0 right-0 bg-[#002147] text-white p-3 flex items-center space-x-3 z-10" data-id="element-6">
      <div className="bg-gray-200 w-12 h-12 rounded overflow-hidden" data-id="element-7">
        <img src={currentPodcast?.image || 'https://uploadthingy.s3.us-west-1.amazonaws.com/opWubLEP9ixdzSjgztUVjg/podcast-img-1.jpg'} alt="Podcast cover" className="w-full h-full object-cover" data-id="element-8" />
      </div>
      <div className="flex-1" data-id="element-9">
        <h4 className="font-bold text-sm text-white" data-id="element-10">
          {currentPodcast?.title || 'The Trivium in Modern Education'}
        </h4>
        <p className="text-xs text-gray-200" data-id="element-11">
          {currentPodcast?.series || 'Classical Education Today'}
        </p>
      </div>
      <div className="flex items-center space-x-2" data-id="element-12">
        <button className="text-white p-2" aria-label="Rewind 30 seconds" data-id="element-13">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-14">
            <path d="M2.5 2v6h6M2.66 15.57a10 10 0 1 0 .57-8.38" data-id="element-15" />
            <path d="M3.5 8v.88a8 8 0 0 0 1.07 3.13" data-id="element-16" />
          </svg>
        </button>
        <button className="text-white p-2" onClick={() => setIsPlaying(!isPlaying)} aria-label={isPlaying ? 'Pause' : 'Play'} data-id="element-17">
          {isPlaying ? <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-18">
              <rect x="6" y="4" width="4" height="16" data-id="element-19"></rect>
              <rect x="14" y="4" width="4" height="16" data-id="element-20"></rect>
            </svg> : <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-21">
              <polygon points="5 3 19 12 5 21 5 3" data-id="element-22"></polygon>
            </svg>}
        </button>
        <button className="text-white p-2" aria-label="Forward 30 seconds" data-id="element-23">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-24">
            <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38" data-id="element-25" />
            <path d="M20.5 8v.88a8 8 0 0 1-1.07 3.13" data-id="element-26" />
          </svg>
        </button>
      </div>
    </div>;
};
// Header Component
const Header = ({
  title
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const showBackButton = location.pathname !== '/' && location.pathname !== '/news' && location.pathname !== '/athletics' && location.pathname !== '/podcasts' && location.pathname !== '/volunteer' && location.pathname !== '/donate' && location.pathname !== '/account';
  return <div className="flex justify-between items-center p-4 border-b" data-id="element-27">
      <div className="w-8" data-id="element-28">
        {showBackButton && <button onClick={() => navigate(-1)} className="text-[#002147]" aria-label="Go back" data-id="element-29">
            <ChevronLeftIcon size={24} data-id="element-30" />
          </button>}
      </div>
      <h1 className="text-xl font-serif text-[#002147] font-bold text-center" data-id="element-31">
        {title}
      </h1>
      <button onClick={() => navigate('/account')} className="text-[#002147]" aria-label="Account" data-id="element-32">
        <UserIcon size={24} data-id="element-33" />
      </button>
    </div>;
};
// Bottom Navigation Component
const BottomNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const navItems = [{
    path: '/news',
    icon: <HomeIcon size={24} data-id="element-34" />,
    label: 'News'
  }, {
    path: '/athletics',
    icon: <TrophyIcon size={24} data-id="element-35" />,
    label: 'Athletics'
  }, {
    path: '/podcasts',
    icon: <RadioIcon size={24} data-id="element-36" />,
    label: 'Podcasts'
  }, {
    path: '/volunteer',
    icon: <HeartHandshakeIcon size={24} data-id="element-37" />,
    label: 'Volunteer'
  }, {
    path: '/more',
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-38">
          <circle cx="5" cy="12" r="1" data-id="element-39" />
          <circle cx="12" cy="12" r="1" data-id="element-40" />
          <circle cx="19" cy="12" r="1" data-id="element-41" />
        </svg>,
    label: 'More'
  }];
  const handleNavClick = path => {
    if (path === '/more') {
      setShowMoreMenu(!showMoreMenu);
    } else {
      navigate(path);
      setShowMoreMenu(false);
    }
  };
  return <>
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex justify-around items-center h-16 px-2 z-10" data-id="element-42">
        {navItems.map(item => <button key={item.path} onClick={() => handleNavClick(item.path)} className={`flex flex-col items-center justify-center w-full h-full ${location.pathname === item.path || item.path === '/more' && showMoreMenu ? 'text-[#002147]' : 'text-gray-500'}`} data-id="element-43">
            <div className={`${location.pathname === item.path || item.path === '/more' && showMoreMenu ? 'text-[#002147]' : 'text-gray-500'}`} data-id="element-44">
              {item.icon}
            </div>
            <span className="text-xs mt-1" data-id="element-45">{item.label}</span>
          </button>)}
      </div>
      {showMoreMenu && <MoreMenu onClose={() => setShowMoreMenu(false)} data-id="element-46" />}
    </>;
};
// More Menu Component
const MoreMenu = ({
  onClose
}) => {
  const navigate = useNavigate();
  const menuItems = [{
    path: '/donate',
    label: 'Donate',
    icon: <GiftIcon size={20} data-id="element-47" />
  }, {
    path: '/directory',
    label: 'Directory',
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-48">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" data-id="element-49" />
          <circle cx="9" cy="7" r="4" data-id="element-50" />
          <path d="M22 21v-2a4 4 0 0 0-3-3.87" data-id="element-51" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" data-id="element-52" />
        </svg>
  }, {
    path: '/calendar',
    label: 'Calendar',
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-53">
          <rect width="18" height="18" x="3" y="4" rx="2" ry="2" data-id="element-54" />
          <line x1="16" x2="16" y1="2" y2="6" data-id="element-55" />
          <line x1="8" x2="8" y1="2" y2="6" data-id="element-56" />
          <line x1="3" x2="21" y1="10" y2="10" data-id="element-57" />
        </svg>
  }, {
    path: '/fine-arts',
    label: 'Fine Arts',
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-58">
          <path d="M9 18V5l12-2v13" data-id="element-59" />
          <circle cx="6" cy="18" r="3" data-id="element-60" />
          <circle cx="18" cy="16" r="3" data-id="element-61" />
        </svg>
  }, {
    path: '/community',
    label: 'Geneva Community Life',
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-62">
          <path d="M12 10c1.7-3.3 4.3-5.5 7.6-6.3 1.5 3.1 1.5 6.5 0 9.6-2.1.7-4.3.7-6.2 0" data-id="element-63" />
          <path d="M12 10c-1.7-3.3-4.3-5.5-7.6-6.3-1.5 3.1-1.5 6.5 0 9.6 2.1.7 4.3.7 6.2 0" data-id="element-64" />
          <path d="M12 10v12" data-id="element-65" />
          <path d="M12 22c4 0 8-1 8-6" data-id="element-66" />
          <path d="M12 22c-4 0-8-1-8-6" data-id="element-67" />
        </svg>
  }, {
    path: '/tuition',
    label: 'Tuition',
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-68">
          <rect width="20" height="14" x="2" y="5" rx="2" data-id="element-69" />
          <line x1="2" x2="22" y1="10" y2="10" data-id="element-70" />
        </svg>
  }];
  const handleItemClick = path => {
    navigate(path);
    onClose();
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 z-20" onClick={onClose} data-id="element-71">
      <div className="absolute bottom-16 left-0 right-0 bg-white rounded-t-lg p-4 max-h-[70vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-72">
        <div className="w-16 h-1 bg-gray-300 rounded-full mx-auto mb-4" data-id="element-73"></div>
        <div className="space-y-4" data-id="element-74">
          {menuItems.map(item => <div key={item.path} className="flex items-center p-2 hover:bg-gray-100 rounded-lg cursor-pointer" onClick={() => handleItemClick(item.path)} data-id="element-75">
              <div className="w-8 h-8 flex items-center justify-center text-[#002147]" data-id="element-76">
                {item.icon}
              </div>
              <span className="ml-3 font-medium" data-id="element-77">{item.label}</span>
            </div>)}
        </div>
      </div>
    </div>;
};
// Directory data - fake names in alphabetical order
const directoryData = [{
  id: 1,
  name: 'Adams, Sarah',
  email: 'sarah.adams@example.com',
  phone: '(555) 123-4567',
  address: '123 Oak St, Boerne, TX 78006'
}, {
  id: 2,
  name: 'Anderson, Michael',
  email: 'michael.anderson@example.com',
  phone: '(555) 234-5678',
  address: '456 Pine Ave, Boerne, TX 78006'
}, {
  id: 3,
  name: 'Baker, Emily',
  email: 'emily.baker@example.com',
  phone: '(555) 345-6789',
  address: '789 Elm Dr, Boerne, TX 78006'
}, {
  id: 4,
  name: 'Bennett, Robert',
  email: 'robert.bennett@example.com',
  phone: '(555) 456-7890',
  address: '101 Maple Ln, Boerne, TX 78006'
}, {
  id: 5,
  name: 'Brooks, Jennifer',
  email: 'jennifer.brooks@example.com',
  phone: '(555) 567-8901',
  address: '202 Cedar Ct, Boerne, TX 78006'
}, {
  id: 6,
  name: 'Brown, David',
  email: 'david.brown@example.com',
  phone: '(555) 678-9012',
  address: '303 Birch Rd, Boerne, TX 78006'
}, {
  id: 7,
  name: 'Campbell, Lisa',
  email: 'lisa.campbell@example.com',
  phone: '(555) 789-0123',
  address: '404 Walnut St, Boerne, TX 78006'
}, {
  id: 8,
  name: 'Carter, James',
  email: 'james.carter@example.com',
  phone: '(555) 890-1234',
  address: '505 Spruce Ave, Boerne, TX 78006'
}, {
  id: 9,
  name: 'Clark, Jessica',
  email: 'jessica.clark@example.com',
  phone: '(555) 901-2345',
  address: '606 Willow Dr, Boerne, TX 78006'
}, {
  id: 10,
  name: 'Collins, John',
  email: 'john.collins@example.com',
  phone: '(555) 012-3456',
  address: '707 Aspen Ln, Boerne, TX 78006'
}, {
  id: 11,
  name: 'Davis, Michelle',
  email: 'michelle.davis@example.com',
  phone: '(555) 123-4567',
  address: '808 Hickory Ct, Boerne, TX 78006'
}, {
  id: 12,
  name: 'Edwards, Thomas',
  email: 'thomas.edwards@example.com',
  phone: '(555) 234-5678',
  address: '909 Sycamore Rd, Boerne, TX 78006'
}, {
  id: 13,
  name: 'Evans, Patricia',
  email: 'patricia.evans@example.com',
  phone: '(555) 345-6789',
  address: '111 Chestnut St, Boerne, TX 78006'
}, {
  id: 14,
  name: 'Foster, William',
  email: 'william.foster@example.com',
  phone: '(555) 456-7890',
  address: '222 Poplar Ave, Boerne, TX 78006'
}, {
  id: 15,
  name: 'Garcia, Elizabeth',
  email: 'elizabeth.garcia@example.com',
  phone: '(555) 567-8901',
  address: '333 Redwood Dr, Boerne, TX 78006'
}, {
  id: 16,
  name: 'Green, Christopher',
  email: 'christopher.green@example.com',
  phone: '(555) 678-9012',
  address: '444 Magnolia Ln, Boerne, TX 78006'
}, {
  id: 17,
  name: 'Hall, Amanda',
  email: 'amanda.hall@example.com',
  phone: '(555) 789-0123',
  address: '555 Dogwood Ct, Boerne, TX 78006'
}, {
  id: 18,
  name: 'Harris, Daniel',
  email: 'daniel.harris@example.com',
  phone: '(555) 890-1234',
  address: '666 Juniper Rd, Boerne, TX 78006'
}, {
  id: 19,
  name: 'Jackson, Stephanie',
  email: 'stephanie.jackson@example.com',
  phone: '(555) 901-2345',
  address: '777 Sequoia St, Boerne, TX 78006'
}, {
  id: 20,
  name: 'Johnson, Matthew',
  email: 'matthew.johnson@example.com',
  phone: '(555) 012-3456',
  address: '888 Cypress Ave, Boerne, TX 78006'
}, {
  id: 21,
  name: 'Jones, Nicole',
  email: 'nicole.jones@example.com',
  phone: '(555) 123-4567',
  address: '999 Hemlock Dr, Boerne, TX 78006'
}, {
  id: 22,
  name: 'King, Andrew',
  email: 'andrew.king@example.com',
  phone: '(555) 234-5678',
  address: '121 Locust Ln, Boerne, TX 78006'
}, {
  id: 23,
  name: 'Lee, Kimberly',
  email: 'kimberly.lee@example.com',
  phone: '(555) 345-6789',
  address: '232 Beech Ct, Boerne, TX 78006'
}, {
  id: 24,
  name: 'Lewis, Joseph',
  email: 'joseph.lewis@example.com',
  phone: '(555) 456-7890',
  address: '343 Alder Rd, Boerne, TX 78006'
}, {
  id: 25,
  name: 'Martin, Melissa',
  email: 'melissa.martin@example.com',
  phone: '(555) 567-8901',
  address: '454 Linden St, Boerne, TX 78006'
}, {
  id: 26,
  name: 'Miller, Joshua',
  email: 'joshua.miller@example.com',
  phone: '(555) 678-9012',
  address: '565 Fir Ave, Boerne, TX 78006'
}, {
  id: 27,
  name: 'Moore, Heather',
  email: 'heather.moore@example.com',
  phone: '(555) 789-0123',
  address: '676 Buckeye Dr, Boerne, TX 78006'
}, {
  id: 28,
  name: 'Nelson, Timothy',
  email: 'timothy.nelson@example.com',
  phone: '(555) 890-1234',
  address: '787 Catalpa Ln, Boerne, TX 78006'
}, {
  id: 29,
  name: 'Parker, Rebecca',
  email: 'rebecca.parker@example.com',
  phone: '(555) 901-2345',
  address: '898 Hawthorn Ct, Boerne, TX 78006'
}, {
  id: 30,
  name: 'Phillips, Brian',
  email: 'brian.phillips@example.com',
  phone: '(555) 012-3456',
  address: '919 Pecan Rd, Boerne, TX 78006'
}, {
  id: 31,
  name: 'Roberts, Laura',
  email: 'laura.roberts@example.com',
  phone: '(555) 123-4567',
  address: '131 Mulberry St, Boerne, TX 78006'
}, {
  id: 32,
  name: 'Robinson, Kevin',
  email: 'kevin.robinson@example.com',
  phone: '(555) 234-5678',
  address: '242 Cottonwood Ave, Boerne, TX 78006'
}, {
  id: 33,
  name: 'Smith, Christina',
  email: 'christina.smith@example.com',
  phone: '(555) 345-6789',
  address: '353 Sassafras Dr, Boerne, TX 78006'
}, {
  id: 34,
  name: 'Taylor, Steven',
  email: 'steven.taylor@example.com',
  phone: '(555) 456-7890',
  address: '464 Tulip Ln, Boerne, TX 78006'
}, {
  id: 35,
  name: 'Thomas, Deborah',
  email: 'deborah.thomas@example.com',
  phone: '(555) 567-8901',
  address: '575 Daisy Ct, Boerne, TX 78006'
}, {
  id: 36,
  name: 'Thompson, Ryan',
  email: 'ryan.thompson@example.com',
  phone: '(555) 678-9012',
  address: '686 Rose Rd, Boerne, TX 78006'
}, {
  id: 37,
  name: 'Walker, Amy',
  email: 'amy.walker@example.com',
  phone: '(555) 789-0123',
  address: '797 Lily St, Boerne, TX 78006'
}, {
  id: 38,
  name: 'White, Jason',
  email: 'jason.white@example.com',
  phone: '(555) 890-1234',
  address: '808 Violet Ave, Boerne, TX 78006'
}, {
  id: 39,
  name: 'Williams, Megan',
  email: 'megan.williams@example.com',
  phone: '(555) 901-2345',
  address: '919 Iris Dr, Boerne, TX 78006'
}, {
  id: 40,
  name: 'Wilson, Eric',
  email: 'eric.wilson@example.com',
  phone: '(555) 012-3456',
  address: '202 Sunflower Ln, Boerne, TX 78006'
}, {
  id: 41,
  name: 'Young, Rachel',
  email: 'rachel.young@example.com',
  phone: '(555) 123-4567',
  address: '313 Marigold Ct, Boerne, TX 78006'
}, {
  id: 42,
  name: 'Zimmerman, Brandon',
  email: 'brandon.zimmerman@example.com',
  phone: '(555) 234-5678',
  address: '424 Daffodil Rd, Boerne, TX 78006'
}];
// Directory Page
const DirectoryPage = () => {
  const navigate = useNavigate();
  // Create an object with alphabetical sections
  const alphabeticalSections = directoryData.reduce((acc, person) => {
    const firstLetter = person.name.charAt(0).toUpperCase();
    if (!acc[firstLetter]) {
      acc[firstLetter] = [];
    }
    acc[firstLetter].push(person);
    return acc;
  }, {});
  // Get all unique first letters sorted
  const sectionLetters = Object.keys(alphabeticalSections).sort();
  return <div className="pb-20 bg-white" data-id="element-78">
      <Header title="Directory" data-id="element-79" />
      <div className="px-4" data-id="element-80">
        {sectionLetters.map(letter => <div key={letter} className="mb-2" data-id="element-81">
            <div className="sticky top-0 bg-gray-100 py-1 px-2 font-bold text-[#002147]" data-id="element-82">
              {letter}
            </div>
            <div className="divide-y" data-id="element-83">
              {alphabeticalSections[letter].map(person => <div key={person.id} className="py-3 flex items-center cursor-pointer" onClick={() => navigate(`/directory/${person.id}`)} data-id="element-84">
                  <div className="w-10 h-10 rounded-full bg-[#002147] text-white flex items-center justify-center mr-3" data-id="element-85">
                    {person.name.split(',')[0].charAt(0)}
                  </div>
                  <div data-id="element-86">
                    <div className="font-medium" data-id="element-87">{person.name}</div>
                    <div className="text-sm text-gray-500" data-id="element-88">{person.phone}</div>
                  </div>
                </div>)}
            </div>
          </div>)}
      </div>
    </div>;
};
// Directory Detail Page
const DirectoryDetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const personId = parseInt(location.pathname.split('/').pop() || '1');
  const person = directoryData.find(p => p.id === personId) || directoryData[0];
  return <div className="pb-20 bg-white" data-id="element-89">
      <Header title="Contact Details" data-id="element-90" />
      <div className="px-4" data-id="element-91">
        <div className="flex justify-center mb-6" data-id="element-92">
          <div className="w-24 h-24 rounded-full bg-[#002147] text-white flex items-center justify-center text-3xl" data-id="element-93">
            {person.name.split(',')[0].charAt(0)}
          </div>
        </div>
        <h2 className="text-xl font-bold text-center mb-6" data-id="element-94">{person.name}</h2>
        <div className="space-y-4" data-id="element-95">
          <div className="border-b pb-3" data-id="element-96">
            <div className="text-sm text-gray-500" data-id="element-97">Phone</div>
            <div className="font-medium" data-id="element-98">{person.phone}</div>
          </div>
          <div className="border-b pb-3" data-id="element-99">
            <div className="text-sm text-gray-500" data-id="element-100">Email</div>
            <div className="font-medium" data-id="element-101">{person.email}</div>
          </div>
          <div className="border-b pb-3" data-id="element-102">
            <div className="text-sm text-gray-500" data-id="element-103">Address</div>
            <div className="font-medium" data-id="element-104">{person.address}</div>
          </div>
        </div>
      </div>
    </div>;
};
// Calendar Page
const CalendarPage = () => {
  return <div className="pb-20 bg-white" data-id="element-105">
      <Header title="Calendar" data-id="element-106" />
      <div className="px-4 py-10 flex items-center justify-center h-64" data-id="element-107">
        <p className="text-xl text-gray-700" data-id="element-108">Demo Page</p>
      </div>
    </div>;
};
// Fine Arts Page
const FineArtsPage = () => {
  return <div className="pb-20 bg-white" data-id="element-109">
      <Header title="Fine Arts" data-id="element-110" />
      <div className="px-4 py-10 flex items-center justify-center h-64" data-id="element-111">
        <p className="text-xl text-gray-700" data-id="element-112">Demo Page</p>
      </div>
    </div>;
};
// Community Life Page
const CommunityLifePage = () => {
  return <div className="pb-20 bg-white" data-id="element-113">
      <Header title="Geneva Community Life" data-id="element-114" />
      <div className="px-4 py-10 flex items-center justify-center h-64" data-id="element-115">
        <p className="text-xl text-gray-700" data-id="element-116">Demo Page</p>
      </div>
    </div>;
};
// Tuition Page
const TuitionPage = () => {
  return <div className="pb-20 bg-white" data-id="element-117">
      <Header title="Tuition" data-id="element-118" />
      <div className="px-4 py-10 flex items-center justify-center h-64" data-id="element-119">
        <p className="text-xl text-gray-700" data-id="element-120">Demo Page</p>
      </div>
    </div>;
};
// News Data
const newsArticles = [{
  id: 1,
  title: 'Class of 2023 Celebrates Commencement',
  summary: 'Geneva graduates toss their caps in celebration after receiving their diplomas at the annual commencement ceremony.',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gFPfu1z7Zq9xMA52hdNzWK/news-1.jpg',
  content: 'The Geneva School of Boerne celebrated the graduation of the Class of 2023 last Saturday at the annual commencement ceremony. Sixty-five seniors received their diplomas in front of proud family, friends, faculty, and staff.\n\nHeadmaster Dr. David Corbin delivered an inspiring address, encouraging graduates to use their classical education as a foundation for lifelong learning and service. Valedictorian Emma Richardson and Salutatorian Michael Chen also spoke, reflecting on their years at Geneva and the close-knit community they\'ve been part of.\n\nThe ceremony concluded with the traditional cap toss, symbolizing the completion of their Geneva journey and the beginning of new adventures. The Class of 2023 has been accepted to over 120 colleges and universities across the nation, with many receiving substantial scholarships.\n\n"This class has shown exceptional academic achievement, leadership, and character," said Upper School Principal Mrs. Jennifer Williams. "We\'re confident they\'ll continue to make a positive impact wherever they go."\n\nThe Geneva community wishes these graduates all the best as they embark on their next chapter.'
}, {
  id: 2,
  title: 'Academic Excellence Awards',
  summary: 'Geneva students proudly display their trophies from the regional academic competition.',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/sdppqKQPzULxWdbHiR47RM/news-2.jpg',
  content: "Geneva School of Boerne students brought home an impressive collection of awards from this year's Regional Academic Competition held in San Antonio last weekend. Competing against fifteen other private schools from across South Texas, Geneva students secured first place overall in the high school division.\n\nThe Logic School team also performed exceptionally well, placing second in their division. Individual achievements included gold medals in Latin, Mathematics, Science, and Rhetoric competitions.\n\n\"Our students' success is a testament to their hard work and the strength of our classical curriculum,\" said Academic Dean Dr. Robert Peterson. \"These competitions allow them to demonstrate the knowledge and skills they've developed through their studies.\"\n\nParticularly noteworthy was junior Hannah Martinez's perfect score in the Advanced Latin translation competition, a feat accomplished only twice before in the competition's history.\n\nThe Geneva community celebrated these achievements at Monday's all-school assembly, where Headmaster Dr. Corbin recognized each participant and medal winner. The students will now prepare for the state competition scheduled for next month in Dallas."
}, {
  id: 3,
  title: 'National Honor Society Induction',
  summary: 'Geneva welcomes new members to the prestigious National Honor Society during the annual induction ceremony.',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/vvRCVYEzGLY5jo3n6rFcJi/news-3.jpg',
  content: 'Twenty-seven Geneva School of Boerne students were inducted into the National Honor Society (NHS) during a solemn ceremony held in the school auditorium last Thursday evening. The event marked the formal recognition of these students\' outstanding achievements in scholarship, leadership, service, and character.\n\nThe candlelight ceremony began with an opening address from NHS Faculty Advisor Mrs. Elizabeth Taylor, who emphasized the responsibility that comes with membership in this prestigious organization. Current NHS members then explained the four pillars of the society before presenting the new inductees.\n\n"Membership in the National Honor Society is not merely an academic recognition but a commitment to continued excellence and service," said Mrs. Taylor. "These students have demonstrated that they embody the values Geneva seeks to instill in all our students."\n\nParents, faculty, and fellow students attended the ceremony to support the inductees. Following the formal induction, a reception was held in the commons area where families and friends could congratulate the new members.\n\nThe newly inducted students will now participate in various service projects throughout the year, including tutoring younger students, organizing community outreach initiatives, and assisting with school events.'
}, {
  id: 4,
  title: 'Rhetoric Competition Highlights',
  summary: 'Senior students demonstrate exceptional public speaking skills at the annual rhetoric showcase.',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/uHjZqvWWHEjpv34gLZnCfp/news-4.jpg',
  content: 'The annual Senior Rhetoric Showcase demonstrated once again the exceptional public speaking abilities developed through Geneva\'s classical curriculum. Held last Friday evening, the event featured twelve seniors presenting original speeches on topics ranging from bioethics to constitutional interpretation.\n\nThe showcase represents the culmination of years of rhetoric training, beginning in Grammar School with memorization and recitation, progressing through Logic School\'s focus on argumentation, and culminating in the Rhetoric School\'s emphasis on persuasive communication.\n\n"What we witnessed tonight was not just impressive public speaking, but clear, logical thinking expressed with conviction and grace," remarked Rhetoric Faculty Chair Dr. Jonathan Miller. "These skills will serve our students well regardless of their future paths."\n\nA panel of judges including faculty, alumni, and community leaders evaluated the presentations. Senior Marcus Thompson received the highest honors for his compelling speech on digital privacy rights in the modern age.\n\n"The ability to articulate ideas clearly and persuasively is increasingly rare in our society," noted guest judge and local attorney Sarah Ramirez. "These students demonstrate a level of rhetorical skill that many professionals would envy."\n\nThe event was well-attended by the Geneva community, with many younger students present to observe the standard they will one day strive to achieve.'
}, {
  id: 5,
  title: 'TAPPS State Champions',
  summary: 'Geneva students bring home multiple TAPPS awards in music and academic competitions.',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bSdBw49b5vui4rcMinyR8A/news-5.jpg',
  content: 'Geneva School of Boerne continued its tradition of excellence at the Texas Association of Private and Parochial Schools (TAPPS) State Academic and Fine Arts Competition, bringing home multiple state championships and placing second overall among 4A schools.\n\nThe school\'s success spanned numerous categories, with state championships in Sacred Music, Poetry Interpretation, and Mathematics. The Chamber Choir\'s rendition of Bach\'s "Jesu, Joy of Man\'s Desiring" earned them a perfect score from all judges, an unprecedented achievement in TAPPS competition history.\n\n"Our students\' success reflects not only their individual talents but also the integration of faith and learning that characterizes a Geneva education," said Fine Arts Director Mrs. Caroline Hughes. "We\'re particularly proud of how our students supported and encouraged one another throughout the competition."\n\nIndividual state champions included senior David Williams in Mathematics, sophomore Sophia Garcia in Poetry Interpretation, and junior Thomas Anderson in Original Oratory. The school also secured second-place finishes in Art History, Latin, and Current Events.\n\n"The breadth of our students\' achievements demonstrates the well-rounded education Geneva provides," noted Academic Dean Dr. Peterson. "From the arts to mathematics to rhetoric, our students excel across disciplines."\n\nThe Geneva community celebrated these accomplishments with a special assembly on Monday, where the state champions performed their winning entries and received recognition from the entire school.'
}];
// News Page Components
const NewsPage = () => {
  const navigate = useNavigate();
  return <div className="pb-20 bg-white" data-id="element-121">
      <Header title="News" data-id="element-122" />
      <div className="px-4 mb-6" onClick={() => navigate(`/news/${newsArticles[0].id}`)} data-id="element-123">
        <img src={newsArticles[0].image} alt={newsArticles[0].title} className="w-full aspect-square object-cover rounded-lg mb-2" data-id="element-124" />
        <h2 className="text-xl font-serif text-[#002147] font-bold" data-id="element-125">
          {newsArticles[0].title}
        </h2>
        <p className="text-gray-700" data-id="element-126">{newsArticles[0].summary}</p>
      </div>
      <div className="px-4 space-y-4" data-id="element-127">
        {newsArticles.slice(1).map(article => <div key={article.id} className="flex items-start space-x-3 pb-4 border-b border-gray-100 cursor-pointer" onClick={() => navigate(`/news/${article.id}`)} data-id="element-128">
            <img src={article.image} alt={article.title} className="w-24 h-24 object-cover rounded flex-shrink-0" data-id="element-129" />
            <div data-id="element-130">
              <h3 className="font-serif text-[#002147] font-bold" data-id="element-131">
                {article.title}
              </h3>
              <p className="text-sm text-gray-700" data-id="element-132">{article.summary}</p>
            </div>
          </div>)}
      </div>
    </div>;
};
// News Detail Page
const NewsDetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const articleId = parseInt(location.pathname.split('/').pop() || '1');
  const article = newsArticles.find(article => article.id === articleId) || newsArticles[0];
  // Find previous and next articles
  const currentIndex = newsArticles.findIndex(a => a.id === article.id);
  const prevArticle = currentIndex > 0 ? newsArticles[currentIndex - 1] : null;
  const nextArticle = currentIndex < newsArticles.length - 1 ? newsArticles[currentIndex + 1] : null;
  return <div className="pb-20 bg-white" data-id="element-133">
      <Header title="News" data-id="element-134" />
      <div className="px-4" data-id="element-135">
        <img src={article.image} alt={article.title} className="w-full aspect-square object-cover rounded-lg mb-4" data-id="element-136" />
        <h2 className="text-xl font-serif text-[#002147] font-bold mb-4" data-id="element-137">
          {article.title}
        </h2>
        <div className="text-gray-700 space-y-4" data-id="element-138">
          {article.content.split('\n\n').map((paragraph, index) => <p key={index} data-id="element-139">{paragraph}</p>)}
        </div>
        {/* Related Articles Section */}
        <div className="mt-8 pt-4 border-t border-gray-200" data-id="element-140">
          <h3 className="font-bold text-lg mb-4" data-id="element-141">Continue Reading</h3>
          <div className="space-y-4" data-id="element-142">
            {prevArticle && <div className="flex items-start space-x-3 pb-4 border-b border-gray-100 cursor-pointer" onClick={() => navigate(`/news/${prevArticle.id}`)} data-id="element-143">
                <img src={prevArticle.image} alt={prevArticle.title} className="w-24 h-24 object-cover rounded flex-shrink-0" data-id="element-144" />
                <div data-id="element-145">
                  <h3 className="font-serif text-[#002147] font-bold" data-id="element-146">
                    {prevArticle.title}
                  </h3>
                  <p className="text-sm text-gray-700" data-id="element-147">{prevArticle.summary}</p>
                </div>
              </div>}
            {nextArticle && <div className="flex items-start space-x-3 pb-4 cursor-pointer" onClick={() => navigate(`/news/${nextArticle.id}`)} data-id="element-148">
                <img src={nextArticle.image} alt={nextArticle.title} className="w-24 h-24 object-cover rounded flex-shrink-0" data-id="element-149" />
                <div data-id="element-150">
                  <h3 className="font-serif text-[#002147] font-bold" data-id="element-151">
                    {nextArticle.title}
                  </h3>
                  <p className="text-sm text-gray-700" data-id="element-152">{nextArticle.summary}</p>
                </div>
              </div>}
          </div>
        </div>
      </div>
    </div>;
};
// Athletics News Data
const athleticsNews = [{
  id: 1,
  title: 'Football Team Advances to Playoffs',
  summary: 'The varsity football team secured their spot in the playoffs with an impressive win against their district rival.',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bSdBw49b5vui4rcMinyR8A/news-5.jpg',
  content: 'The Geneva School of Boerne varsity football team has secured their spot in the TAPPS playoffs after a decisive 35-14 victory over district rival St. Mary\'s Hall last Friday night.\n\nLed by senior quarterback Jacob Mitchell, who threw for 280 yards and three touchdowns, the Eagles dominated from the opening kickoff. The defense was equally impressive, forcing four turnovers and limiting St. Mary\'s Hall to just 200 total yards of offense.\n\n"I\'m incredibly proud of how our team has come together this season," said head coach Mark Williams. "These young men have worked tirelessly since August, and it\'s gratifying to see their efforts rewarded with a playoff berth."\n\nThe Eagles will enter the playoffs as the second seed from their district, setting up a first-round matchup against Trinity Christian Academy from Dallas. The game will be played at home next Friday at 7:30 PM.\n\nAthletic Director Sarah Johnson encouraged the Geneva community to come out and support the team. "The atmosphere at our home games has been electric this season, and we want to maintain that energy for the playoffs. Our players feed off that enthusiasm."\n\nTickets for the playoff game will be available for purchase through the school\'s website starting Monday morning.'
}, {
  id: 2,
  title: 'Cross Country Takes District Title',
  summary: 'Both boys and girls cross country teams dominated at the district meet, qualifying for regionals next month.',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/sdppqKQPzULxWdbHiR47RM/news-2.jpg',
  content: 'The Geneva School of Boerne cross country program continues its tradition of excellence as both the boys\' and girls\' teams claimed district championships at last Saturday\'s TAPPS District 5 meet held at Comfort High School.\n\nThe girls\' team, led by junior Emma Richardson\'s first-place individual finish, placed all five scoring runners in the top 15. Richardson completed the 5K course in 18:42, a new personal best and course record. Sophomores Olivia Chen and Madison Taylor finished third and fourth, respectively.\n\n\"Our girls have shown remarkable dedication all season,\" said coach Jennifer Adams. \"They\'ve been putting in early morning practices since July, and that commitment is evident in their performance.\"\n\nOn the boys\' side, senior Thomas Anderson led the way with a second-place finish, completing the course in 16:28. The Eagles secured the team title by placing four runners in the top 10, edging out rival TMI Episcopal by just six points.\n\n\"This was truly a team victory,\" Anderson said. \"We\'ve pushed each other in practice all season, and that competitive spirit helped us today.\"\n\nBoth teams will advance to the regional meet scheduled for November 12 at McAllister Park in San Antonio. The top four teams at regionals will qualify for the state championship meet in Waco later next month.\n\nAthletic Director Sarah Johnson praised both teams for their accomplishments. \"Our cross country program exemplifies the values we strive to instill in all our student-athletes: dedication, perseverance, and sportsmanship.\"'
}];
// Athletics Page
const AthleticsPage = () => {
  const navigate = useNavigate();
  const recentGames = [{
    id: 1,
    team: 'Varsity Football',
    opponent: "St. Mary's Hall",
    date: 'Oct 15',
    score: '28-14',
    result: 'Win',
    location: 'Home'
  }, {
    id: 2,
    team: 'Varsity Volleyball',
    opponent: 'TMI Episcopal',
    date: 'Oct 12',
    score: '3-1',
    result: 'Win',
    location: 'Away'
  }, {
    id: 3,
    team: 'JV Basketball',
    opponent: 'San Antonio Christian',
    date: 'Oct 10',
    score: '62-58',
    result: 'Win',
    location: 'Home'
  }, {
    id: 4,
    team: 'Varsity Soccer',
    opponent: 'Boerne High',
    date: 'Oct 8',
    score: '1-2',
    result: 'Loss',
    location: 'Away'
  }];
  const upcomingGames = [{
    id: 5,
    team: 'Varsity Football',
    opponent: 'TMI Episcopal',
    date: 'Oct 22',
    time: '7:00 PM',
    location: 'Home'
  }, {
    id: 6,
    team: 'Varsity Volleyball',
    opponent: 'Cornerstone Christian',
    date: 'Oct 24',
    time: '6:30 PM',
    location: 'Away'
  }, {
    id: 7,
    team: 'JV Basketball',
    opponent: 'Regents School',
    date: 'Oct 26',
    time: '5:00 PM',
    location: 'Home'
  }, {
    id: 8,
    team: 'Varsity Soccer',
    opponent: 'Holy Cross',
    date: 'Oct 29',
    time: '4:30 PM',
    location: 'Away'
  }];
  const [showingPast, setShowingPast] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentGames = showingPast ? recentGames : upcomingGames;
  const currentGame = currentGames[currentIndex];
  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else if (showingPast && currentIndex === 0) {
      // If we're at the first past game, stay there
    } else if (!showingPast && currentIndex === 0) {
      // If we're at the first upcoming game, switch to past games
      setShowingPast(true);
      setCurrentIndex(recentGames.length - 1);
    }
  };
  const goToNext = () => {
    if (currentIndex < currentGames.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else if (showingPast && currentIndex === recentGames.length - 1) {
      // If we're at the last past game, switch to upcoming games
      setShowingPast(false);
      setCurrentIndex(0);
    } else if (!showingPast && currentIndex === upcomingGames.length - 1) {
      // If we're at the last upcoming game, stay there
    }
  };
  return <div className="pb-20 bg-white" data-id="element-153">
      <Header title="Athletics" data-id="element-154" />
      <div className="mb-6" data-id="element-155">
        <div className="bg-[#002147] py-6 px-4" data-id="element-156">
          <div className="flex justify-between items-center mb-4" data-id="element-157">
            <button onClick={goToPrevious} className="text-white p-1" disabled={showingPast && currentIndex === 0} data-id="element-158">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-159">
                <path d="M15 18l-6-6 6-6" data-id="element-160" />
              </svg>
            </button>
            {currentGame && <div className="text-white text-center flex-1" data-id="element-161">
                <div className="font-bold text-lg mb-1" data-id="element-162">{currentGame.team}</div>
                <div className="text-gray-300 mb-3" data-id="element-163">
                  vs {currentGame.opponent}
                </div>
                {showingPast ? <div className="flex justify-center items-center" data-id="element-164">
                    <span className="text-xl font-bold mr-3" data-id="element-165">
                      {currentGame.score}
                    </span>
                    <span className={`text-sm font-bold px-2 py-1 rounded ${currentGame.result === 'Win' ? 'bg-green-700' : 'bg-red-700'}`} data-id="element-166">
                      {currentGame.result}
                    </span>
                  </div> : <div className="text-xl font-bold" data-id="element-167">{currentGame.time}</div>}
                <div className="text-gray-300 mt-2" data-id="element-168">
                  {currentGame.date} • {currentGame.location}
                </div>
              </div>}
            <button onClick={goToNext} className="text-white p-1" disabled={!showingPast && currentIndex === upcomingGames.length - 1} data-id="element-169">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-170">
                <path d="M9 18l6-6-6-6" data-id="element-171" />
              </svg>
            </button>
          </div>
          <div className="flex justify-center" data-id="element-172">
            {currentGames.map((_, idx) => <div key={idx} className={`h-1 w-6 mx-1 rounded-full ${idx === currentIndex ? 'bg-white' : 'bg-gray-500'}`} data-id="element-173" />)}
          </div>
        </div>
        <div className="flex justify-center space-x-2 mt-4" data-id="element-174">
          <button onClick={() => {
          setShowingPast(true);
          setCurrentIndex(0);
        }} className={`px-3 py-1 text-sm rounded ${showingPast ? 'bg-[#002147] text-white' : 'bg-gray-200 text-gray-700'}`} data-id="element-175">
            Past
          </button>
          <button onClick={() => {
          setShowingPast(false);
          setCurrentIndex(0);
        }} className={`px-3 py-1 text-sm rounded ${!showingPast ? 'bg-[#002147] text-white' : 'bg-gray-200 text-gray-700'}`} data-id="element-176">
            Upcoming
          </button>
        </div>
      </div>
      <div className="px-4" data-id="element-177">
        <h2 className="text-lg font-bold mb-4" data-id="element-178">Athletics News</h2>
        <div className="space-y-4" data-id="element-179">
          {athleticsNews.map(article => <div key={article.id} className="flex items-start space-x-3 pb-4 border-b border-gray-100 cursor-pointer" onClick={() => navigate(`/athletics/news/${article.id}`)} data-id="element-180">
              <img src={article.image} alt={article.title} className="w-24 h-24 object-cover rounded flex-shrink-0" data-id="element-181" />
              <div data-id="element-182">
                <h3 className="font-serif text-[#002147] font-bold" data-id="element-183">
                  {article.title}
                </h3>
                <p className="text-sm text-gray-700" data-id="element-184">{article.summary}</p>
              </div>
            </div>)}
        </div>
      </div>
    </div>;
};
// Athletics News Detail Page
const AthleticsNewsDetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const articleId = parseInt(location.pathname.split('/').pop() || '1');
  const article = athleticsNews.find(article => article.id === articleId) || athleticsNews[0];
  // Find previous and next articles
  const currentIndex = athleticsNews.findIndex(a => a.id === article.id);
  const prevArticle = currentIndex > 0 ? athleticsNews[currentIndex - 1] : null;
  const nextArticle = currentIndex < athleticsNews.length - 1 ? athleticsNews[currentIndex + 1] : null;
  return <div className="pb-20 bg-white" data-id="element-185">
      <Header title="Athletics" data-id="element-186" />
      <div className="px-4" data-id="element-187">
        <img src={article.image} alt={article.title} className="w-full aspect-square object-cover rounded-lg mb-4" data-id="element-188" />
        <h2 className="text-xl font-serif text-[#002147] font-bold mb-4" data-id="element-189">
          {article.title}
        </h2>
        <div className="text-gray-700 space-y-4" data-id="element-190">
          {article.content.split('\n\n').map((paragraph, index) => <p key={index} data-id="element-191">{paragraph}</p>)}
        </div>
        {/* Related Articles Section */}
        <div className="mt-8 pt-4 border-t border-gray-200" data-id="element-192">
          <h3 className="font-bold text-lg mb-4" data-id="element-193">Continue Reading</h3>
          <div className="space-y-4" data-id="element-194">
            {prevArticle && <div className="flex items-start space-x-3 pb-4 border-b border-gray-100 cursor-pointer" onClick={() => navigate(`/athletics/news/${prevArticle.id}`)} data-id="element-195">
                <img src={prevArticle.image} alt={prevArticle.title} className="w-24 h-24 object-cover rounded flex-shrink-0" data-id="element-196" />
                <div data-id="element-197">
                  <h3 className="font-serif text-[#002147] font-bold" data-id="element-198">
                    {prevArticle.title}
                  </h3>
                  <p className="text-sm text-gray-700" data-id="element-199">{prevArticle.summary}</p>
                </div>
              </div>}
            {nextArticle && <div className="flex items-start space-x-3 pb-4 cursor-pointer" onClick={() => navigate(`/athletics/news/${nextArticle.id}`)} data-id="element-200">
                <img src={nextArticle.image} alt={nextArticle.title} className="w-24 h-24 object-cover rounded flex-shrink-0" data-id="element-201" />
                <div data-id="element-202">
                  <h3 className="font-serif text-[#002147] font-bold" data-id="element-203">
                    {nextArticle.title}
                  </h3>
                  <p className="text-sm text-gray-700" data-id="element-204">{nextArticle.summary}</p>
                </div>
              </div>}
          </div>
        </div>
      </div>
    </div>;
};
// Podcasts Page Components
const PodcastsPage = ({
  isPlaying,
  setIsPlaying,
  currentPodcast,
  setCurrentPodcast
}) => {
  const navigate = useNavigate();
  const podcasts = [{
    id: 1,
    title: 'The Trivium in Modern Education',
    series: 'Classical Education Today',
    description: 'In this episode, we explore how the classical Trivium—Grammar, Logic, and Rhetoric—applies to modern educational practices. Our guest Dr. Elizabeth Miller shares insights from her 20 years of experience implementing classical education methods in contemporary classrooms.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/opWubLEP9ixdzSjgztUVjg/podcast-img-1.jpg'
  }, {
    id: 2,
    title: 'Virtue Formation in the Classroom',
    series: 'Classical Education Today',
    description: 'How do we cultivate virtue in students? This episode features a conversation with headmaster Jonathan Edwards on integrating character formation into academic subjects and school culture.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/opWubLEP9ixdzSjgztUVjg/podcast-img-1.jpg'
  }, {
    id: 3,
    title: 'Faith and Learning Integration',
    series: 'Classical Education Today',
    description: "Explore how Christian worldview seamlessly integrates with classical learning. Our panel discusses practical approaches to helping students see God's truth in all subjects.",
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/opWubLEP9ixdzSjgztUVjg/podcast-img-1.jpg'
  }, {
    id: 4,
    title: 'Great Books for Young Readers',
    series: 'Classical Education Today',
    description: 'Literature professor Dr. Sarah Thompson shares her curated list of timeless books for elementary and middle school students, explaining why these works resonate with young minds and help form their moral imagination.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/opWubLEP9ixdzSjgztUVjg/podcast-img-1.jpg'
  }];
  const genrevaConversations = [{
    id: 5,
    title: 'Parenting with Classical Principles',
    series: 'Geneva Conversations',
    description: 'A practical discussion on applying classical education principles at home. Learn how to create a home environment that reinforces what students are learning in the classroom.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/opWubLEP9ixdzSjgztUVjg/podcast-img-1.jpg'
  }, {
    id: 6,
    title: 'Athletics and Virtue',
    series: 'Geneva Conversations',
    description: "Geneva's athletic director discusses how sports programs can be structured to build character and reinforce classical values of excellence, teamwork, and perseverance.",
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/opWubLEP9ixdzSjgztUVjg/podcast-img-1.jpg'
  }, {
    id: 7,
    title: 'Art and Beauty in Education',
    series: 'Geneva Conversations',
    description: "Why is beauty essential to education? Our fine arts faculty explain how exposure to beautiful art, music, and literature shapes students' souls and enriches their education.",
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/opWubLEP9ixdzSjgztUVjg/podcast-img-1.jpg'
  }, {
    id: 8,
    title: 'The Role of Latin',
    series: 'Geneva Conversations',
    description: 'Latin instructor Mrs. Williams explains why Latin remains relevant and valuable in modern classical education, offering cognitive benefits and deeper understanding of English.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/opWubLEP9ixdzSjgztUVjg/podcast-img-1.jpg'
  }, {
    id: 9,
    title: 'Technology in Classical Schools',
    series: 'Geneva Conversations',
    description: 'How should classical schools approach technology? This episode explores a balanced approach that leverages useful tools while maintaining the human element of education.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/opWubLEP9ixdzSjgztUVjg/podcast-img-1.jpg'
  }, {
    id: 10,
    title: 'College Preparation',
    series: 'Geneva Conversations',
    description: 'Our college counselors discuss how classical education prepares students for university success, with testimonials from recent graduates thriving in college environments.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/opWubLEP9ixdzSjgztUVjg/podcast-img-1.jpg'
  }];
  const handlePlayPause = (podcast, e) => {
    e.stopPropagation();
    if (isPlaying && currentPodcast?.id === podcast.id) {
      // If this podcast is already playing, pause it
      setIsPlaying(false);
    } else {
      // Otherwise start playing this podcast
      setIsPlaying(true);
      setCurrentPodcast(podcast);
    }
  };
  return <div className="pb-32 bg-white" data-id="element-205">
      <Header title="Podcasts" data-id="element-206" />
      <div className="mb-6" data-id="element-207">
        <h2 className="text-lg font-bold px-4 mb-2" data-id="element-208">Your Subscriptions</h2>
        <div className="flex overflow-x-auto px-4 space-x-4 pb-4" data-id="element-209">
          {podcasts.map(podcast => <div key={podcast.id} className="flex-shrink-0 w-64 cursor-pointer" onClick={() => navigate(`/podcasts/${podcast.id}`)} data-id="element-210">
              <div className="bg-gray-200 w-full aspect-square mb-2 rounded-lg" data-id="element-211">
                <img src={podcast.image} alt={podcast.title} className="w-full h-full object-cover rounded-lg" data-id="element-212" />
              </div>
              <h3 className="font-bold text-[#002147]" data-id="element-213">{podcast.series}</h3>
              <p className="text-sm text-gray-700" data-id="element-214">{podcast.title}</p>
              <button className="mt-2 text-sm font-bold text-[#002147] flex items-center" onClick={e => handlePlayPause(podcast, e)} data-id="element-215">
                {isPlaying && currentPodcast?.id === podcast.id ? <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1" data-id="element-216">
                      <rect x="6" y="4" width="4" height="16" data-id="element-217"></rect>
                      <rect x="14" y="4" width="4" height="16" data-id="element-218"></rect>
                    </svg>
                    Pause
                  </> : <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1" data-id="element-219">
                      <polygon points="5 3 19 12 5 21 5 3" data-id="element-220"></polygon>
                    </svg>
                    Play
                  </>}
              </button>
            </div>)}
        </div>
      </div>
      <div className="px-4" data-id="element-221">
        <h2 className="text-lg font-bold mb-4" data-id="element-222">All Podcasts</h2>
        <div className="grid grid-cols-2 gap-4" data-id="element-223">
          {genrevaConversations.map(podcast => <div key={podcast.id} className="mb-4 cursor-pointer" onClick={() => navigate(`/podcasts/${podcast.id}`)} data-id="element-224">
              <div className="bg-gray-200 w-full aspect-square rounded-lg" data-id="element-225">
                <img src={podcast.image} alt={podcast.title} className="w-full h-full object-cover rounded-lg" data-id="element-226" />
              </div>
              <h3 className="font-bold mt-2 text-[#002147]" data-id="element-227">
                {podcast.series}
              </h3>
              <p className="text-xs text-gray-700" data-id="element-228">{podcast.title}</p>
            </div>)}
        </div>
      </div>
    </div>;
};
// Podcast Detail Page
const PodcastDetailPage = ({
  isPlaying,
  setIsPlaying,
  currentPodcast,
  setCurrentPodcast
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const podcastId = parseInt(location.pathname.split('/').pop() || '1');
  // Combine all podcasts for easy lookup
  const podcasts = [{
    id: 1,
    title: 'The Trivium in Modern Education',
    series: 'Classical Education Today',
    description: 'In this episode, we explore how the classical Trivium—Grammar, Logic, and Rhetoric—applies to modern educational practices. Our guest Dr. Elizabeth Miller shares insights from her 20 years of experience implementing classical education methods in contemporary classrooms.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/opWubLEP9ixdzSjgztUVjg/podcast-img-1.jpg'
  }, {
    id: 2,
    title: 'Virtue Formation in the Classroom',
    series: 'Classical Education Today',
    description: 'How do we cultivate virtue in students? This episode features a conversation with headmaster Jonathan Edwards on integrating character formation into academic subjects and school culture.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/opWubLEP9ixdzSjgztUVjg/podcast-img-1.jpg'
  }, {
    id: 3,
    title: 'Faith and Learning Integration',
    series: 'Classical Education Today',
    description: "Explore how Christian worldview seamlessly integrates with classical learning. Our panel discusses practical approaches to helping students see God's truth in all subjects.",
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/opWubLEP9ixdzSjgztUVjg/podcast-img-1.jpg'
  }, {
    id: 4,
    title: 'Great Books for Young Readers',
    series: 'Classical Education Today',
    description: 'Literature professor Dr. Sarah Thompson shares her curated list of timeless books for elementary and middle school students, explaining why these works resonate with young minds and help form their moral imagination.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/opWubLEP9ixdzSjgztUVjg/podcast-img-1.jpg'
  }, {
    id: 5,
    title: 'Parenting with Classical Principles',
    series: 'Geneva Conversations',
    description: 'A practical discussion on applying classical education principles at home. Learn how to create a home environment that reinforces what students are learning in the classroom.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/opWubLEP9ixdzSjgztUVjg/podcast-img-1.jpg'
  }, {
    id: 6,
    title: 'Athletics and Virtue',
    series: 'Geneva Conversations',
    description: "Geneva's athletic director discusses how sports programs can be structured to build character and reinforce classical values of excellence, teamwork, and perseverance.",
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/opWubLEP9ixdzSjgztUVjg/podcast-img-1.jpg'
  }, {
    id: 7,
    title: 'Art and Beauty in Education',
    series: 'Geneva Conversations',
    description: "Why is beauty essential to education? Our fine arts faculty explain how exposure to beautiful art, music, and literature shapes students' souls and enriches their education.",
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/opWubLEP9ixdzSjgztUVjg/podcast-img-1.jpg'
  }, {
    id: 8,
    title: 'The Role of Latin',
    series: 'Geneva Conversations',
    description: 'Latin instructor Mrs. Williams explains why Latin remains relevant and valuable in modern classical education, offering cognitive benefits and deeper understanding of English.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/opWubLEP9ixdzSjgztUVjg/podcast-img-1.jpg'
  }, {
    id: 9,
    title: 'Technology in Classical Schools',
    series: 'Geneva Conversations',
    description: 'How should classical schools approach technology? This episode explores a balanced approach that leverages useful tools while maintaining the human element of education.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/opWubLEP9ixdzSjgztUVjg/podcast-img-1.jpg'
  }, {
    id: 10,
    title: 'College Preparation',
    series: 'Geneva Conversations',
    description: 'Our college counselors discuss how classical education prepares students for university success, with testimonials from recent graduates thriving in college environments.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/opWubLEP9ixdzSjgztUVjg/podcast-img-1.jpg'
  }];
  const podcast = podcasts.find(p => p.id === podcastId) || podcasts[0];
  const handlePlayPause = e => {
    e.stopPropagation();
    if (isPlaying && currentPodcast?.id === podcast.id) {
      // If this podcast is already playing, pause it
      setIsPlaying(false);
    } else {
      // Otherwise start playing this podcast
      setIsPlaying(true);
      setCurrentPodcast(podcast);
    }
  };
  return <div className="pb-32 bg-white" data-id="element-229">
      <Header title="Podcasts" data-id="element-230" />
      <div className="px-4" data-id="element-231">
        <img src={podcast.image} alt={podcast.title} className="w-full aspect-square object-cover rounded-lg mb-4" data-id="element-232" />
        <h2 className="text-xl font-bold text-[#002147] mb-1" data-id="element-233">
          {podcast.title}
        </h2>
        <p className="text-gray-600 mb-4" data-id="element-234">{podcast.series}</p>
        <button className="w-full py-3 bg-[#C7B37F] text-[#002147] rounded-lg font-bold mb-4 flex items-center justify-center" onClick={handlePlayPause} data-id="element-235">
          {isPlaying && currentPodcast?.id === podcast.id ? <>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2" data-id="element-236">
                <rect x="6" y="4" width="4" height="16" data-id="element-237"></rect>
                <rect x="14" y="4" width="4" height="16" data-id="element-238"></rect>
              </svg>
              Pause Episode
            </> : <>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2" data-id="element-239">
                <polygon points="5 3 19 12 5 21 5 3" data-id="element-240"></polygon>
              </svg>
              Play Episode
            </>}
        </button>
        <h3 className="font-bold text-[#002147] mb-2" data-id="element-241">Episode Description</h3>
        <p className="text-gray-700" data-id="element-242">{podcast.description}</p>
      </div>
    </div>;
};
// Volunteer Page Components
const VolunteerPage = () => {
  const navigate = useNavigate();
  const volunteerOptions = [{
    id: 1,
    title: 'Lunch Duty',
    description: 'Help supervise students during lunch periods'
  }, {
    id: 2,
    title: 'Campus Cleanup',
    description: 'Join our monthly campus beautification efforts'
  }, {
    id: 3,
    title: 'Field Trip Chaperone',
    description: 'Accompany students on educational outings'
  }, {
    id: 4,
    title: 'Field Trip Driver',
    description: 'Provide transportation for student field trips'
  }, {
    id: 5,
    title: 'Recess Monitor',
    description: 'Supervise students during recess periods'
  }, {
    id: 6,
    title: 'Sports Event Volunteer',
    description: 'Assist with home games and athletic events'
  }];
  return <div className="pb-20 bg-white" data-id="element-243">
      <Header title="Volunteer" data-id="element-244" />
      <div className="px-4" data-id="element-245">
        <div className="divide-y" data-id="element-246">
          {volunteerOptions.map(option => <div key={option.id} className="py-4 cursor-pointer" onClick={() => navigate(`/volunteer/${option.id}`)} data-id="element-247">
              <h2 className="font-bold text-[#002147]" data-id="element-248">{option.title}</h2>
              <p className="text-gray-700 text-sm" data-id="element-249">{option.description}</p>
            </div>)}
        </div>
      </div>
    </div>;
};
// Volunteer Detail Page
const VolunteerDetailPage = () => {
  const [showForm, setShowForm] = useState(false);
  return <div className="pb-20 bg-white" data-id="element-250">
      <Header title="Lunch Duty" data-id="element-251" />
      <div className="p-4" data-id="element-252">
        <h2 className="text-lg font-bold mb-2" data-id="element-253">Description</h2>
        <p className="mb-4 text-gray-700" data-id="element-254">
          Help supervise students during lunch periods. Volunteers will assist
          faculty in maintaining order and cleanliness in the cafeteria while
          providing a positive presence for students.
        </p>
        <div className="mb-6" data-id="element-255">
          <h3 className="font-bold text-sm text-gray-600" data-id="element-256">Date & Time</h3>
          <p className="text-[#002147]" data-id="element-257">Weekdays, 11:30 AM - 1:00 PM</p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg mb-6" data-id="element-258">
          <p className="text-sm mb-2" data-id="element-259">You will sign up as:</p>
          <p className="font-bold" data-id="element-260">John Smith</p>
        </div>
        <div className="flex space-x-3" data-id="element-261">
          <button className="bg-[#002147] text-white py-2 px-4 rounded font-bold flex-1" data-id="element-262">
            Sign Up
          </button>
          <button className="border border-[#002147] text-[#002147] py-2 px-4 rounded font-bold" onClick={() => setShowForm(true)} data-id="element-263">
            Register Someone Else
          </button>
        </div>
      </div>
      {showForm && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" data-id="element-264">
          <div className="bg-white rounded-lg p-6 w-full max-w-md" data-id="element-265">
            <h2 className="text-lg font-bold mb-4" data-id="element-266">Register Someone Else</h2>
            <form className="space-y-4" data-id="element-267">
              <div data-id="element-268">
                <label className="block text-sm font-bold mb-1" data-id="element-269">Name</label>
                <input type="text" className="w-full border rounded p-2" data-id="element-270" />
              </div>
              <div data-id="element-271">
                <label className="block text-sm font-bold mb-1" data-id="element-272">Email</label>
                <input type="email" className="w-full border rounded p-2" data-id="element-273" />
              </div>
              <div data-id="element-274">
                <label className="block text-sm font-bold mb-1" data-id="element-275">Phone</label>
                <input type="tel" className="w-full border rounded p-2" data-id="element-276" />
              </div>
              <div data-id="element-277">
                <label className="block text-sm font-bold mb-1" data-id="element-278">Address</label>
                <input type="text" className="w-full border rounded p-2" data-id="element-279" />
              </div>
              <div data-id="element-280">
                <label className="block text-sm font-bold mb-1" data-id="element-281">Age</label>
                <input type="number" className="w-full border rounded p-2" data-id="element-282" />
              </div>
              <div className="flex space-x-3 pt-2" data-id="element-283">
                <button type="submit" className="bg-[#002147] text-white py-2 px-4 rounded font-bold flex-1" data-id="element-284">
                  Register
                </button>
                <button type="button" className="border border-gray-300 py-2 px-4 rounded font-bold" onClick={() => setShowForm(false)} data-id="element-285">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>}
    </div>;
};
// Donate Page
const DonatePage = () => {
  return <div className="pb-20 bg-white" data-id="element-286">
      <Header title="Donate" data-id="element-287" />
      <div className="px-4" data-id="element-288">
        <div className="mb-8" data-id="element-289">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/g4gEPf1J1F25W6MQmHg53a/Geneva_Logo_-_Print_500px.jpg" alt="Geneva School of Boerne Logo" className="w-32 h-auto mx-auto mb-6" data-id="element-290" />
          <p className="text-center text-gray-700 leading-relaxed mb-8" data-id="element-291">
            Your gifts to Geneva School of Boerne enable us to fulfill our
            mission and to serve families in the Texas Hill Country as we aim to
            train committed Christians whose lives are marked by virtue: faith,
            hope, love, courage, justice, discipline and wisdom. Thank you for
            your generosity and mission partnership.
          </p>
          <button className="bg-[#C7B37F] text-[#002147] py-3 px-6 rounded-lg font-bold text-center w-full" onClick={() => window.open('https://www.genevaschooltx.org/give-generalfund/', '_blank')} data-id="element-292">
            Make a Donation
          </button>
        </div>
      </div>
    </div>;
};
// Account Page
const AccountPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  return <div className="pb-20 bg-white" data-id="element-293">
      <Header title="Account" data-id="element-294" />
      <div className="px-4" data-id="element-295">
        <div className="flex justify-center mb-6" data-id="element-296">
          <div className="flex border border-[#002147] rounded-lg overflow-hidden" data-id="element-297">
            <button className={`px-6 py-2 font-bold ${isLogin ? 'bg-[#002147] text-white' : 'text-[#002147]'}`} onClick={() => setIsLogin(true)} data-id="element-298">
              Sign In
            </button>
            <button className={`px-6 py-2 font-bold ${!isLogin ? 'bg-[#002147] text-white' : 'text-[#002147]'}`} onClick={() => setIsLogin(false)} data-id="element-299">
              Register
            </button>
          </div>
        </div>
        {isLogin ? <form className="space-y-4" data-id="element-300">
            <div data-id="element-301">
              <label className="block text-sm font-bold mb-1" data-id="element-302">
                Email or Phone
              </label>
              <input type="text" className="w-full border rounded p-3" data-id="element-303" />
            </div>
            <div data-id="element-304">
              <label className="block text-sm font-bold mb-1" data-id="element-305">Password</label>
              <input type="password" className="w-full border rounded p-3" data-id="element-306" />
            </div>
            <div className="text-center" data-id="element-307">
              <button type="button" className="text-[#002147] text-sm underline" data-id="element-308">
                Or receive a one-time password by phone
              </button>
            </div>
            <button type="submit" className="bg-[#002147] text-white py-3 px-4 rounded font-bold w-full mt-4" data-id="element-309">
              Sign In
            </button>
          </form> : <form className="space-y-4" data-id="element-310">
            <div data-id="element-311">
              <label className="block text-sm font-bold mb-1" data-id="element-312">Full Name</label>
              <input type="text" className="w-full border rounded p-3" data-id="element-313" />
            </div>
            <div data-id="element-314">
              <label className="block text-sm font-bold mb-1" data-id="element-315">Email</label>
              <input type="email" className="w-full border rounded p-3" data-id="element-316" />
            </div>
            <div data-id="element-317">
              <label className="block text-sm font-bold mb-1" data-id="element-318">Phone</label>
              <input type="tel" className="w-full border rounded p-3" data-id="element-319" />
            </div>
            <div data-id="element-320">
              <label className="block text-sm font-bold mb-1" data-id="element-321">Address</label>
              <input type="text" className="w-full border rounded p-3" data-id="element-322" />
            </div>
            <div data-id="element-323">
              <label className="block text-sm font-bold mb-1" data-id="element-324">
                Date of Birth
              </label>
              <input type="date" className="w-full border rounded p-3" data-id="element-325" />
            </div>
            <div data-id="element-326">
              <label className="block text-sm font-bold mb-1" data-id="element-327">Password</label>
              <input type="password" className="w-full border rounded p-3" data-id="element-328" />
            </div>
            <button type="submit" className="bg-[#002147] text-white py-3 px-4 rounded font-bold w-full mt-4" data-id="element-329">
              Register
            </button>
          </form>}
      </div>
    </div>;
};
// Main App Component
export function App() {
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentPodcast, setCurrentPodcast] = useState(null);
  useEffect(() => {
    // Simulate loading time
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);
  if (loading) {
    return <LoadingScreen data-id="element-330" />;
  }
  return <Router data-id="element-331">
      <div className="bg-white min-h-screen font-sans" data-id="element-332">
        <Routes data-id="element-333">
          <Route path="/" element={<NewsPage data-id="element-335" />} data-id="element-334" />
          <Route path="/news" element={<NewsPage data-id="element-337" />} data-id="element-336" />
          <Route path="/news/:id" element={<NewsDetailPage data-id="element-339" />} data-id="element-338" />
          <Route path="/athletics" element={<AthleticsPage data-id="element-341" />} data-id="element-340" />
          <Route path="/athletics/news/:id" element={<AthleticsNewsDetailPage data-id="element-343" />} data-id="element-342" />
          <Route path="/podcasts" element={<PodcastsPage isPlaying={isPlaying} setIsPlaying={setIsPlaying} currentPodcast={currentPodcast} setCurrentPodcast={setCurrentPodcast} data-id="element-345" />} data-id="element-344" />
          <Route path="/podcasts/:id" element={<PodcastDetailPage isPlaying={isPlaying} setIsPlaying={setIsPlaying} currentPodcast={currentPodcast} setCurrentPodcast={setCurrentPodcast} data-id="element-347" />} data-id="element-346" />
          <Route path="/volunteer" element={<VolunteerPage data-id="element-349" />} data-id="element-348" />
          <Route path="/volunteer/:id" element={<VolunteerDetailPage data-id="element-351" />} data-id="element-350" />
          <Route path="/donate" element={<DonatePage data-id="element-353" />} data-id="element-352" />
          <Route path="/account" element={<AccountPage data-id="element-355" />} data-id="element-354" />
          {/* New routes */}
          <Route path="/directory" element={<DirectoryPage data-id="element-357" />} data-id="element-356" />
          <Route path="/directory/:id" element={<DirectoryDetailPage data-id="element-359" />} data-id="element-358" />
          <Route path="/calendar" element={<CalendarPage data-id="element-361" />} data-id="element-360" />
          <Route path="/fine-arts" element={<FineArtsPage data-id="element-363" />} data-id="element-362" />
          <Route path="/community" element={<CommunityLifePage data-id="element-365" />} data-id="element-364" />
          <Route path="/tuition" element={<TuitionPage data-id="element-367" />} data-id="element-366" />
        </Routes>
        <NowPlayingBar isPlaying={isPlaying} setIsPlaying={setIsPlaying} currentPodcast={currentPodcast} data-id="element-368" />
        <BottomNavigation data-id="element-369" />
      </div>
    </Router>;
}